import { InlineCell } from '../../../cdl/TableBuilder/components/InlineCell';
import { StyledInlineHeaderCell } from '../../../cdl/TableBuilder/components/StyledInlineHeaderCell';
import { StyledInlineTableBodyRow } from '../../../cdl/TableBuilder/components/StyledInlineTableBodyRow';
import { StyledTable } from '../../../cdl/TableBuilder/components/StyledTable';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { useEnums } from '../../../common/hooks/useEnums';
import { useProductStringFormat } from '../../../common/hooks/useProductStringFormat';
import { ItemModel } from '../../../common/models/ItemModel';
import { SamplekitModel } from '../../../common/models/SamplekitModel';
import { getCellEmphasis } from '../../../common/ReadOnlyProductTable/components/getCellEmphasis';

interface EnquiredProductsTableProps {
    items: ItemModel[];
    sampleKits: SamplekitModel[];
}

export const EnquiredProductsTable = ({ items, sampleKits }: EnquiredProductsTableProps) => {
    const { getHumanReadableValue } = useEnums();

    const formatProductString = useProductStringFormat();

    const columns = [
        { header: translate('order.pos'), numeric: true },
        { header: translate('order.product') },
        { header: translate('order.productgroup') },
        { header: translate('order.packtype') },
        { header: translate('order.unitsize'), numeric: true },
        { header: translate('order.quantity'), numeric: true },
        { header: translate('order.volume'), numeric: true },
        { header: translate('order.unit') },
    ];

    return (
        <StyledTable variant="inline">
            <thead>
                <tr>
                    {columns.map((column, index) => (
                        <StyledInlineHeaderCell key={index} textAlign={column.numeric ? 'right' : 'left'}>
                            {column.header}
                        </StyledInlineHeaderCell>
                    ))}
                </tr>
            </thead>
            <tbody>
                {items.map((item, index) => (
                    <StyledInlineTableBodyRow disabled={item.deleted} key={index}>
                        <InlineCell numeric>{index + 1}</InlineCell>
                        <InlineCell>
                            {!item.isTranslationMissing()
                                ? formatProductString(item.product!)
                                : translate('global.translationmissing')}
                        </InlineCell>
                        <InlineCell>
                            {!item.isTranslationMissing()
                                ? getHumanReadableValue('productGroup', item.product!.group)
                                : getHumanReadableValue('productGroup', item.baseProduct?.group)}
                        </InlineCell>
                        <InlineCell emphasis={getCellEmphasis(item.changes.packTypeChanged)}>
                            {getHumanReadableValue('packType', item.packType)}
                        </InlineCell>
                        <InlineCell emphasis={getCellEmphasis(item.changes.unitSizeChanged)} numeric>
                            {formatNumber({ number: item.unitSize, maximumFractionDigits: 3 })}
                        </InlineCell>
                        <InlineCell emphasis={getCellEmphasis(item.changes.unitsChanged)} numeric>
                            {item.units ? formatNumber({ number: item.units }) : Characters.EN_DASH}
                        </InlineCell>
                        <InlineCell numeric>
                            {item.volume
                                ? formatNumber({ number: item.volume, maximumFractionDigits: 3 })
                                : Characters.EN_DASH}
                        </InlineCell>
                        <InlineCell emphasis={getCellEmphasis(item.changes.unitChanged)}>
                            {getHumanReadableValue('productUnit', item.unit)}
                        </InlineCell>
                    </StyledInlineTableBodyRow>
                ))}

                {sampleKits.length
                    ? sampleKits.map((sampleKit, index) => (
                          <StyledInlineTableBodyRow key={index}>
                              <InlineCell numeric>{items.length + index + 1}</InlineCell>
                              <InlineCell emphasis={getCellEmphasis(sampleKit.changes.nameChanged)}>
                                  {sampleKit.name || Characters.EN_DASH}
                              </InlineCell>
                              <InlineCell>{translate('order.samplekit.title')}</InlineCell>
                              <InlineCell colSpan={2}>{translate('order.samplekit.packtype')}</InlineCell>
                              <InlineCell emphasis={getCellEmphasis(sampleKit.changes.quantityChanged)} numeric>
                                  {sampleKit.quantity || Characters.EN_DASH}
                              </InlineCell>
                              <InlineCell colSpan={2}></InlineCell>
                          </StyledInlineTableBodyRow>
                      ))
                    : null}
            </tbody>
        </StyledTable>
    );
};
